import sharesproApi from 'rtkQuery/sharespro/sharesproApiService';
import { SharesproApiResponse } from 'rtkQuery/sharespro/types/sharesproApiResponse';
import mapArticleListItemFromApi from 'rtkQuery/sharespro/mapper/modules/articles/mapArticleListItemFromApi';
import mapPaginationListFromApi from 'rtkQuery/sharespro/mapper/shared/mapPaginationListFromApi';
import { mapCommentItemFromApi } from 'rtkQuery/sharespro/mapper/shared/mapCommentsFromApi';
import paginationMergeResponse from 'rtkQuery/utils/paginationMergeResponse';

const setReactionToArticle = (
  article: ArticleListItem,
  newReaction: Reaction
): ReactionListItem[] => {
  let isNewReaction = true;
  const reactions = article.reactions
    .map((reaction) => {
      if (reaction.reaction.id === newReaction.id) {
        isNewReaction = false;

        return {
          ...reaction,
          // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
          total: reaction.total + 1,
        };
      } else if (reaction.reaction.id === article.selectedReactionId) {
        return {
          ...reaction,
          total: reaction.total - 1,
        };
      } else {
        return reaction;
      }
    })
    .filter((reaction) => reaction.total > 0);
  if (isNewReaction) {
    reactions.push({
      reaction: newReaction,
      total: 1,
    });
  }

  return reactions;
};

const deleteReactionFromArticle = (
  article: ArticleListItem
): ReactionListItem[] => {
  const reactions = article.reactions
    .map((reaction) => {
      if (reaction.reaction.id === article.selectedReactionId) {
        return {
          ...reaction,
          total: reaction.total - 1,
        };
      } else {
        return reaction;
      }
    })
    .filter((reaction) => reaction.total > 0);

  return reactions;
};
const addVoteToArticle = (article: ArticleListItem, answerId: number): Poll => {
  const answers = article.poll?.answers.map((element) => {
    if (element.id === answerId) {
      return {
        ...element,
        isVotedByInvestor: true,
        voteCount: (element.voteCount ?? 0) + 1,
      };
    } else {
      return element;
    }
  });

  return {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    ...article.poll!,
    totalVotes: (article.poll?.totalVotes ?? 0) + 1,
    answers: answers ?? [],
  };
};

const deleteVoteFromArticle = (article: ArticleListItem): Poll => {
  const answers = article.poll?.answers.map((element) => {
    if (element.isVotedByInvestor === true) {
      return {
        ...element,
        isVotedByInvestor: false,
        voteCount: (element.voteCount ?? 0) - 1,
      };
    } else {
      return element;
    }
  });

  return {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    ...article.poll!,
    totalVotes: (article.poll?.totalVotes ?? 0) - 1,
    answers: answers ?? [],
  };
};

export const articlesApi = sharesproApi.injectEndpoints({
  endpoints: (builder) => ({
    fetchPrivateChannelArticles: builder.query<
      PaginationList<ArticleListItem>,
      ApiPaginationQueryParams
    >({
      query: (params) => ({
        url: '/v3/site/media/private-articles',
        method: 'GET',
        sendToken: true,
        params,
      }),
      transformResponse(baseQueryReturnValue: SharesproApiResponse<any>) {
        return mapPaginationListFromApi(
          baseQueryReturnValue.data.articles,
          mapArticleListItemFromApi
        );
      },
    }),
    fetchArticleDetail: builder.query<ArticleListItem, ID>({
      query: (id) => ({
        url: '/v3/site/media/article',
        method: 'GET',
        sendToken: true,
        params: {
          id,
        },
      }),
      transformResponse(baseQueryReturnValue: SharesproApiResponse<any>) {
        return mapArticleListItemFromApi(baseQueryReturnValue.data.article);
      },
    }),
    addVoteArticle: builder.mutation<void, SendVoteRequestData>({
      query: (data) => ({
        url: '/v3/site/vote/send',
        method: 'POST',
        sendToken: true,
        params: {
          poll_id: data.poll_id,
        },
        body: {
          answer_id: data.answer_id,
        },
      }),
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        dispatch(
          articlesApi.util.updateQueryData(
            'fetchArticleDetail',
            data.article_id,
            (draft) => {
              draft.poll = addVoteToArticle(draft, data.answer_id ?? -1);
            }
          )
        );
        dispatch(
          articlesApi.util.updateQueryData(
            'fetchPrivateChannelArticles',
            { page: data.page },
            (draft) => {
              draft.items = draft.items.map((article) => {
                if (article.id === data.article_id) {
                  return {
                    ...article,
                    poll: addVoteToArticle(article, data.answer_id ?? -1),
                  };
                } else {
                  return article;
                }
              });
            }
          )
        );
        await queryFulfilled;
      },
    }),
    deleteVoteArticle: builder.mutation<void, SendVoteRequestData>({
      query: (data) => ({
        url: '/v3/site/vote/decline',
        method: 'DELETE',
        sendToken: true,
        params: {
          poll_id: data.poll_id,
        },
      }),
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        dispatch(
          articlesApi.util.updateQueryData(
            'fetchArticleDetail',
            data.article_id,
            (draft) => {
              draft.poll = deleteVoteFromArticle(draft);
            }
          )
        );
        dispatch(
          articlesApi.util.updateQueryData(
            'fetchPrivateChannelArticles',
            { page: data.page },
            (draft) => {
              draft.items = draft.items.map((article) => {
                if (article.id === data.article_id) {
                  return {
                    ...article,
                    poll: deleteVoteFromArticle(article),
                  };
                } else {
                  return article;
                }
              });
            }
          )
        );
        await queryFulfilled;
      },
    }),
    setArticleReaction: builder.mutation<
      void,
      ApiArticleAddReactionRequestData
    >({
      query: (data) => ({
        url: '/v3/site/article/reaction',
        method: 'POST',
        sendToken: true,
        params: {
          article_id: data.article_id,
        },
        body: {
          reaction: data.reaction.id,
        },
      }),
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        dispatch(
          articlesApi.util.updateQueryData(
            'fetchArticleDetail',
            data.article_id,
            (draft) => {
              draft.reactions = setReactionToArticle(draft, data.reaction);
              draft.selectedReactionId = data.reaction.id;
            }
          )
        );
        dispatch(
          articlesApi.util.updateQueryData(
            'fetchPrivateChannelArticles',
            { page: data.page },
            (draft) => {
              draft.items = draft.items.map((article) => {
                if (article.id === data.article_id) {
                  return {
                    ...article,
                    selectedReactionId: data.reaction.id,
                    reactions: setReactionToArticle(article, data.reaction),
                  };
                } else {
                  return article;
                }
              });
            }
          )
        );
        await queryFulfilled;
      },
    }),
    deleteArticleReaction: builder.mutation<
      void,
      ApiArticleDeleteReactionRequestData
    >({
      query: (articleId) => ({
        url: '/v3/site/article/remove-reaction',
        method: 'DELETE',
        sendToken: true,
        params: {
          article_id: articleId,
        },
      }),
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        dispatch(
          articlesApi.util.updateQueryData(
            'fetchArticleDetail',
            data.article_id,
            (draft) => {
              draft.reactions = deleteReactionFromArticle(draft);
              draft.selectedReactionId = null;
            }
          )
        );
        dispatch(
          articlesApi.util.updateQueryData(
            'fetchPrivateChannelArticles',
            { page: data.page },
            (draft) => {
              draft.items = draft.items.map((article) => {
                if (article.id === data.article_id) {
                  return {
                    ...article,
                    selectedReactionId: null,
                    reactions: deleteReactionFromArticle(article),
                  };
                } else {
                  return article;
                }
              });
            }
          )
        );
        await queryFulfilled;
      },
    }),
    fetchComments: builder.query<
      ArticleCommentList,
      ApiArticleCommentListRequestParams
    >({
      query: (params) => ({
        url: '/v3/site/article/comment-list',
        method: 'GET',
        sendToken: true,
        params,
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<
          ApiPaginationListResponse<ApiComment>
        >,
        meta,
        arg
      ) {
        return {
          ...mapPaginationListFromApi(
            baseQueryReturnValue.data,
            mapCommentItemFromApi
          ),
          articleId: arg.article_id,
        };
      },
      serializeQueryArgs: ({ queryArgs }) => {
        return { chat_id: queryArgs.article_id };
      },
      merge: paginationMergeResponse,
      providesTags: (result) => {
        return [
          {
            type: 'ArticleComments',
            id: result?.articleId,
          },
        ];
      },
      forceRefetch({ currentArg, previousArg }) {
        return (
          currentArg?.page !== previousArg?.page ||
          currentArg?.article_id !== previousArg?.article_id
        );
      },
    }),
    addArticleComment: builder.mutation<
      CommentListItem,
      ApiAddArticleCommentRequestData
    >({
      query: (data) => ({
        url: '/v3/site/article/comment',
        method: 'PUT',
        sendToken: true,
        params: {
          article_id: data.article_id,
        },
        body: {
          text: data.text,
        },
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<ApiComment>
      ) {
        return mapCommentItemFromApi(baseQueryReturnValue.data);
      },
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        const resp = await queryFulfilled;
        dispatch(
          articlesApi.util.updateQueryData(
            'fetchComments',
            { page: 1, article_id: data.article_id },
            (draft) => {
              draft.items.push(resp.data);
              draft.itemsCount++;
            }
          )
        );
      },
    }),
    updateArticleComment: builder.mutation<
      CommentListItem,
      ApiUpdateArticleCommentRequestData
    >({
      query: (data) => ({
        url: '/v3/site/article/update-comment',
        method: 'PATCH',
        sendToken: true,
        params: {
          comment_id: data.comment_id,
        },
        body: {
          text: data.text,
        },
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<ApiComment>
      ) {
        return mapCommentItemFromApi(baseQueryReturnValue.data);
      },
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        const resp = await queryFulfilled;
        dispatch(
          articlesApi.util.updateQueryData(
            'fetchComments',
            { page: 1, article_id: data.article_id },
            (draft) => {
              draft.items = draft.items.map((comment) => {
                if (comment.id === data.comment_id) {
                  return resp.data;
                } else {
                  return comment;
                }
              });
            }
          )
        );
      },
    }),
    deleteArticleComment: builder.mutation<
      void,
      ApiDeleteArticleCommentRequestData
    >({
      query: (data) => ({
        url: '/v3/site/article/remove-comment',
        method: 'DELETE',
        sendToken: true,
        params: {
          comment_id: data.comment_id,
        },
      }),
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(
          articlesApi.util.updateQueryData(
            'fetchComments',
            { page: 1, article_id: data.article_id },
            (draft) => {
              draft.items = draft.items.filter((comment) => {
                return comment.id !== data.comment_id;
              });
              draft.itemsCount--;
            }
          )
        );
      },
    }),
    privateChannelLead: builder.mutation<void, void>({
      query: () => ({
        url: '/v3/site/media/private-channel-lead',
        method: 'POST',
        sendToken: true,
      }),
    }),
  }),
});

export const {
  useFetchPrivateChannelArticlesQuery,
  useFetchArticleDetailQuery,
  useAddVoteArticleMutation,
  useDeleteVoteArticleMutation,
  useDeleteArticleReactionMutation,
  useSetArticleReactionMutation,
  useFetchCommentsQuery,
  useAddArticleCommentMutation,
  useDeleteArticleCommentMutation,
  useUpdateArticleCommentMutation,
  usePrivateChannelLeadMutation,
} = articlesApi;
